import Header from "@/components/Header";
import Footer from "@/components/Footer";

const LAST_UPDATED = "February 20, 2026";

export default function Terms() {
    return (
        <div className="min-h-screen bg-black text-white" data-testid="terms-page">
            <Header />
            <main className="max-w-4xl mx-auto px-6 sm:px-10 py-16">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-3">
                    LEGAL
                </div>
                <h1 className="font-anton text-4xl sm:text-5xl uppercase leading-[0.95] mb-2">
                    Terms of Service
                </h1>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-10">
                    LAST UPDATED · {LAST_UPDATED}
                </div>

                <div className="prose prose-invert max-w-none space-y-6 text-white/80 font-sans leading-relaxed">
                    <p>
                        These Terms of Service (&quot;Terms&quot;) govern your use of Higher Power Movement
                        (&quot;HPM&quot;, the &quot;Service&quot;), a product of Higher Power Movement. By accessing
                        or using the Service, you agree to be bound by these Terms.
                    </p>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">1. Not Legal Advice</h2>
                    <p>
                        <strong>HPM is NOT a law firm and does not provide legal advice or legal representation.</strong>
                        All AI-generated analyses, blog content, demand letters, and form outputs are
                        legal information only and are not a substitute for advice from a licensed
                        attorney in your jurisdiction. Use of the Service does not create an
                        attorney-client relationship between you and HPM or Higher Power Movement.
                    </p>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">2. Eligibility</h2>
                    <p>
                        You must be at least 16 years old to use the Service. By using it, you
                        represent that you have the legal capacity to enter into this agreement.
                    </p>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">3. Accounts</h2>
                    <p>
                        You are responsible for maintaining the confidentiality of any login
                        credentials and for all activity that occurs under your account. Notify us
                        immediately at <a className="text-[#FF3B30] underline" href="mailto:hello@alphacovenantholdings.com">hello@alphacovenantholdings.com</a>
                        of any unauthorized access.
                    </p>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">4. Subscriptions &amp; Billing</h2>
                    <ul className="list-disc pl-6 space-y-2">
                        <li>Subscription tiers (Starter, Professional, Elite, Enterprise) bill monthly or annually via Stripe, with auto-renewal at the same interval until cancelled.</li>
                        <li>You may cancel at any time from the Firm Portal. Cancellation stops future renewals; access continues through the end of the current paid period.</li>
                        <li>Annual plans are non-refundable after 30 days. Monthly plans are not pro-rated on cancellation.</li>
                        <li>Failed payments may result in suspension of the Service. We will attempt to notify you before suspension.</li>
                        <li>Stripe charges and refunds appear on your statement as &quot;ALPHA COVENANT&quot; or &quot;HPM*COURTROOM&quot; depending on the product.</li>
                    </ul>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">5. HPM Courtroom Founding-Member Presale</h2>
                    <ul className="list-disc pl-6 space-y-2">
                        <li>The $99 founding-member presale is a ONE-TIME charge that locks in lifetime founding-member pricing on the Solo tier when HPM Courtroom launches.</li>
                        <li>If HPM Courtroom does not launch within 18 months of your payment, you are entitled to a full refund of the $99.</li>
                        <li>Founding-member pricing is non-transferable.</li>
                        <li>See our <a className="text-[#FF3B30] underline" href="/refund-policy">Refund Policy</a> for full terms.</li>
                    </ul>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">6. Acceptable Use</h2>
                    <p>You agree NOT to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                        <li>Use the Service to impersonate any person or entity.</li>
                        <li>Submit case details that you know to be false.</li>
                        <li>Attempt to reverse-engineer, scrape, or overload our endpoints.</li>
                        <li>Use the Service for any unlawful purpose, including practicing law without a license.</li>
                        <li>Resell or rebrand outputs of the Service without written permission.</li>
                    </ul>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">7. Intellectual Property</h2>
                    <p>
                        All software, designs, and original content remain the property of Alpha
                        Covenant Holdings. You retain ownership of your submissions. You grant us
                        a limited, non-exclusive license to process your submissions for the
                        purpose of delivering the Service.
                    </p>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">8. Termination</h2>
                    <p>
                        We may suspend or terminate accounts that violate these Terms. You may
                        delete your account at any time by emailing us. Termination does not
                        relieve you of payment obligations already incurred.
                    </p>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">9. Disclaimers &amp; Limitation of Liability</h2>
                    <p>
                        The Service is provided &quot;AS IS&quot; without warranties of any kind. To the
                        maximum extent permitted by law, Higher Power Movement and HPM are not
                        liable for any indirect, incidental, special, or consequential damages,
                        including loss of profits, data, or business arising out of your use of
                        the Service. Our total aggregate liability is limited to the amount you
                        paid us in the 12 months preceding the claim, or USD $100, whichever is
                        greater.
                    </p>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">10. Indemnification</h2>
                    <p>
                        You agree to indemnify and hold harmless Higher Power Movement, HPM,
                        and their affiliates from claims arising out of your misuse of the
                        Service or violation of these Terms.
                    </p>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">11. Governing Law &amp; Disputes</h2>
                    <p>
                        These Terms are governed by the laws of the State of Delaware, USA,
                        without regard to conflict-of-laws principles. Any disputes shall be
                        resolved by binding arbitration in Delaware under JAMS rules, except that
                        either party may bring small-claims actions in any court of competent
                        jurisdiction.
                    </p>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">12. Changes</h2>
                    <p>
                        We may update these Terms. Material changes will be emailed to active
                        subscribers. Continued use of the Service after changes constitutes
                        acceptance.
                    </p>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">13. Contact</h2>
                    <p>
                        Higher Power Movement · <a className="text-[#FF3B30] underline" href="mailto:hello@alphacovenantholdings.com">hello@alphacovenantholdings.com</a>
                    </p>
                </div>
            </main>
            <Footer disclaimer="HPM provides legal information, not legal advice. Consult a licensed attorney for legal advice specific to your situation." />
        </div>
    );
}
